import dayjs from "dayjs";
import { CHART_COLOR_PALETTE } from "src/components/charts/chartPalette";
import { seededRatio, seededSwing } from "src/utils/seededMockRandom";

/**
 * Centralised mock dataset for the TikTok and Snapchat organic reports. Period
 * totals come from one profile per platform and every content row, daily point
 * and type breakdown is derived from it, so the KPIs, charts and table always
 * agree. Values are seeded, never random.
 */

export type OrganicSocialPlatform = "tiktok" | "snapchat";

export interface SocialContentTypeDefinition {
  /** Post-type slug, localised through `formatOrganicPostTypeLabel`. */
  key: string;
  color: string;
  /** Share of the content published in the period. */
  contentShare: number;
  /** Reach strength relative to the platform average. */
  performanceIndex: number;
  /** Engagement rate relative to the platform average. */
  engagementIndex: number;
  /** Click-through rate relative to the platform average. */
  clickIndex: number;
}

interface SocialProfile {
  /** Follower count at the end of the period. */
  followers: number;
  /** Followers gained over a 30-day period. */
  followerGrowth: number;
  /** Pieces of content published over a 30-day period. */
  contentCreated: number;
  views: number;
  impressions: number;
  /** Engagements as a percentage of impressions. */
  engagementRate: number;
  /** Link clicks as a percentage of impressions. */
  ctr: number;
  /** How engagements split across likes, comments and shares. */
  engagementMix: { likes: number; comments: number; shares: number };
  /** Daily compounding growth, measured from the anchor date. */
  growth: number;
  volatility: number;
  contentTypes: SocialContentTypeDefinition[];
  /** Used to build believable content links. */
  handle: string;
}

const TREND_ANCHOR_DATE = "2026-08-27";
const PROFILE_PERIOD_DAYS = 30;

const PROFILES: Record<OrganicSocialPlatform, SocialProfile> = {
  tiktok: {
    followers: 148_500,
    followerGrowth: 7_850,
    contentCreated: 34,
    views: 1_280_000,
    impressions: 1_760_000,
    engagementRate: 5.8,
    ctr: 1.4,
    engagementMix: { likes: 0.78, comments: 0.06, shares: 0.16 },
    growth: 0.0036,
    volatility: 0.22,
    handle: "datatramdemo",
    contentTypes: [
      {
        key: "video",
        color: CHART_COLOR_PALETTE[0],
        contentShare: 0.68,
        performanceIndex: 1.16,
        engagementIndex: 1.12,
        clickIndex: 1.02,
      },
      {
        key: "carousel",
        color: CHART_COLOR_PALETTE[1],
        contentShare: 0.2,
        performanceIndex: 0.84,
        engagementIndex: 0.95,
        clickIndex: 1.18,
      },
      {
        key: "photo",
        color: CHART_COLOR_PALETTE[2],
        contentShare: 0.12,
        performanceIndex: 0.63,
        engagementIndex: 0.72,
        clickIndex: 0.8,
      },
    ],
  },
  snapchat: {
    followers: 96_400,
    followerGrowth: 4_120,
    contentCreated: 42,
    views: 720_000,
    impressions: 1_090_000,
    engagementRate: 3.9,
    ctr: 1.1,
    engagementMix: { likes: 0.72, comments: 0.08, shares: 0.2 },
    growth: 0.0018,
    volatility: 0.26,
    handle: "datatram.demo",
    contentTypes: [
      {
        key: "story",
        color: CHART_COLOR_PALETTE[0],
        contentShare: 0.48,
        performanceIndex: 1.04,
        engagementIndex: 0.94,
        clickIndex: 1.0,
      },
      {
        key: "spotlight",
        color: CHART_COLOR_PALETTE[1],
        contentShare: 0.19,
        performanceIndex: 1.28,
        engagementIndex: 1.36,
        clickIndex: 0.85,
      },
      {
        key: "video",
        color: CHART_COLOR_PALETTE[2],
        contentShare: 0.21,
        performanceIndex: 0.94,
        engagementIndex: 1.05,
        clickIndex: 1.12,
      },
      {
        key: "image",
        color: CHART_COLOR_PALETTE[3],
        contentShare: 0.12,
        performanceIndex: 0.6,
        engagementIndex: 0.7,
        clickIndex: 0.9,
      },
    ],
  },
};

/**
 * A normal-looking brand feed with Saudi-relevant moments mixed in rather than
 * every post being seasonal.
 */
const CONTENT_THUMB_BASE = "/images/social-content";

const CONTENT_ITEMS: Array<{ caption: string; thumb: string }> = [
  { caption: "POV: you found the abaya you’ve been looking for 🤍", thumb: "abaya" },
  { caption: "GRWM for a Riyadh night out ✨", thumb: "styling" },
  { caption: "New Eid Collection — first look", thumb: "eid" },
  { caption: "Styling three ways: one kaftan", thumb: "styling" },
  { caption: "Ramadan Edit: modest layering", thumb: "ramadan" },
  { caption: "Save this for your next occasion", thumb: "gift" },
  { caption: "Customer favourites this week", thumb: "styling" },
  { caption: "Restock alert 🚨", thumb: "restock" },
  { caption: "A little green for Saudi National Day 💚", thumb: "national-day" },
  { caption: "What’s in our Ramadan moodboard?", thumb: "ramadan" },
  { caption: "New Oud Collection unboxing", thumb: "oud" },
  { caption: "Saudi National Day Collection", thumb: "national-day" },
  { caption: "Meet the design team", thumb: "studio" },
  { caption: "Jeddah pop-up highlights", thumb: "riyadh" },
  { caption: "Care guide: keeping silk fresh", thumb: "abaya" },
  { caption: "Five looks under SAR 400", thumb: "styling" },
  { caption: "Gift guide: for her", thumb: "gift" },
  { caption: "Dammam delivery, same day", thumb: "restock" },
  { caption: "Our most-loved scarf, restyled", thumb: "styling" },
];

export interface SocialDailyPoint {
  date: string;
  views: number;
  impressions: number;
  engagements: number;
  likes: number;
  comments: number;
  shares: number;
  linkClicks: number;
  followerGain: number;
  followers: number;
  engagementRate: number | null;
  ctr: number | null;
}

export interface SocialTotals {
  followers: number;
  followerGrowth: number;
  contentCreated: number;
  views: number;
  impressions: number;
  engagements: number;
  likes: number;
  comments: number;
  shares: number;
  linkClicks: number;
  engagementRate: number | null;
  ctr: number | null;
}

export interface SocialContentRow {
  id: string;
  caption: string;
  typeKey: string;
  thumbnailUrl: string;
  publishedAt: string;
  impressions: number;
  views: number;
  linkClicks: number;
  likes: number;
  comments: number;
  shares: number;
  engagementRate: number | null;
  ctr: number | null;
  link: string;
}

export interface SocialTypePerformance {
  key: string;
  color: string;
  contentCount: number;
  views: number;
  impressions: number;
  likes: number;
  comments: number;
  shares: number;
  linkClicks: number;
  engagementRate: number | null;
  ctr: number | null;
}

export interface SocialReportBundle {
  totals: SocialTotals;
  priorTotals: SocialTotals;
  daily: SocialDailyPoint[];
  typePerformance: SocialTypePerformance[];
  content: SocialContentRow[];
}

const MAX_DAYS = 400;

const round2 = (value: number): number => Math.round(value * 100) / 100;

const percent = (part: number, whole: number): number | null =>
  whole > 0 ? round2((part / whole) * 100) : null;

const dateRange = (startDate: string, endDate: string): string[] => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return [];
  }
  const dates: string[] = [];
  let cursor = start;
  while (!cursor.isAfter(end) && dates.length < MAX_DAYS) {
    dates.push(cursor.format("YYYY-MM-DD"));
    cursor = cursor.add(1, "day");
  }
  return dates;
};

const priorPeriod = (startDate: string, endDate: string) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) {
    return { startDate, endDate };
  }
  const days = Math.max(1, end.diff(start, "day") + 1);
  return {
    startDate: start.subtract(days, "day").format("YYYY-MM-DD"),
    endDate: start.subtract(1, "day").format("YYYY-MM-DD"),
  };
};

/** Growth measured from the anchor, so an earlier window sits lower. */
const growthFactor = (platform: OrganicSocialPlatform, date: string): number => {
  const days = dayjs(date).diff(dayjs(TREND_ANCHOR_DATE), "day");
  return Math.min(3, Math.max(0.3, Math.pow(1 + PROFILES[platform].growth, days)));
};

const dailySeries = (
  platform: OrganicSocialPlatform,
  startDate: string,
  endDate: string
): SocialDailyPoint[] => {
  const profile = PROFILES[platform];
  const dates = dateRange(startDate, endDate);
  if (!dates.length) {
    return [];
  }

  const perDay = {
    views: profile.views / PROFILE_PERIOD_DAYS,
    impressions: profile.impressions / PROFILE_PERIOD_DAYS,
    followerGain: profile.followerGrowth / PROFILE_PERIOD_DAYS,
  };

  // Jitter is normalised to a mean of 1 so the window still lands on the
  // profile's period totals; growth is applied on top of it.
  const jitters = dates.map(
    date => 1 + seededSwing(`${platform}|day|${date}`) * profile.volatility
  );
  const jitterMean = jitters.reduce((sum, value) => sum + value, 0) / jitters.length;

  const raw = dates.map((date, index) => {
    const factor = (jitters[index] / (jitterMean || 1)) * growthFactor(platform, date);
    const impressions = Math.round(perDay.impressions * factor);
    const viewSwing = 1 + seededSwing(`${platform}|views|${date}`) * 0.1;
    const views = Math.round(perDay.views * factor * viewSwing);
    const engagementSwing = 1 + seededSwing(`${platform}|eng|${date}`) * 0.18;
    const engagements = Math.round((impressions * profile.engagementRate * engagementSwing) / 100);
    const clickSwing = 1 + seededSwing(`${platform}|clicks|${date}`) * 0.16;
    const linkClicks = Math.round((impressions * profile.ctr * clickSwing) / 100);

    return {
      date,
      views,
      impressions,
      engagements,
      likes: Math.round(engagements * profile.engagementMix.likes),
      comments: Math.round(engagements * profile.engagementMix.comments),
      shares: Math.round(engagements * profile.engagementMix.shares),
      linkClicks,
      followerGain: Math.round(perDay.followerGain * factor),
      engagementRate: percent(engagements, impressions),
      ctr: percent(linkClicks, impressions),
    };
  });

  // Followers run up to the profile's headline figure at the anchor date, so a
  // window that ends earlier finishes on a proportionally lower count.
  const daysAfterWindow = Math.max(
    0,
    dayjs(TREND_ANCHOR_DATE).diff(dayjs(dates[dates.length - 1]), "day")
  );
  const endOfWindow = Math.round(
    profile.followers - perDay.followerGain * daysAfterWindow
  );
  const totalGain = raw.reduce((sum, point) => sum + point.followerGain, 0);
  let running = endOfWindow - totalGain;

  return raw.map(point => {
    running += point.followerGain;
    return { ...point, followers: running };
  });
};

const sumDaily = (
  platform: OrganicSocialPlatform,
  daily: SocialDailyPoint[],
  contentCreated: number
): SocialTotals => {
  const totals = daily.reduce(
    (accumulator, point) => ({
      views: accumulator.views + point.views,
      impressions: accumulator.impressions + point.impressions,
      engagements: accumulator.engagements + point.engagements,
      likes: accumulator.likes + point.likes,
      comments: accumulator.comments + point.comments,
      shares: accumulator.shares + point.shares,
      linkClicks: accumulator.linkClicks + point.linkClicks,
      followerGrowth: accumulator.followerGrowth + point.followerGain,
    }),
    {
      views: 0,
      impressions: 0,
      engagements: 0,
      likes: 0,
      comments: 0,
      shares: 0,
      linkClicks: 0,
      followerGrowth: 0,
    }
  );

  return {
    ...totals,
    followers: daily.length ? daily[daily.length - 1].followers : PROFILES[platform].followers,
    contentCreated,
    engagementRate: percent(totals.engagements, totals.impressions),
    ctr: percent(totals.linkClicks, totals.impressions),
  };
};

const contentCountFor = (platform: OrganicSocialPlatform, days: number): number =>
  Math.max(1, Math.round((PROFILES[platform].contentCreated * days) / PROFILE_PERIOD_DAYS));

/** Assigns content types by their published share, largest first. */
const typeForIndex = (
  types: SocialContentTypeDefinition[],
  index: number,
  count: number
): SocialContentTypeDefinition => {
  let cursor = 0;
  for (const type of types) {
    cursor += Math.max(1, Math.round(type.contentShare * count));
    if (index < cursor) {
      return type;
    }
  }
  return types[types.length - 1];
};

const buildContent = (
  platform: OrganicSocialPlatform,
  totals: SocialTotals,
  startDate: string,
  endDate: string
): SocialContentRow[] => {
  const profile = PROFILES[platform];
  const dates = dateRange(startDate, endDate);
  const count = contentCountFor(platform, dates.length || PROFILE_PERIOD_DAYS);

  const seeds = Array.from({ length: count }, (_unused, index) => {
    const type = typeForIndex(profile.contentTypes, index, count);
    const seed = `${platform}|content|${index}`;
    const reach = type.performanceIndex * (0.55 + seededRatio(seed) * 0.9);
    const item = CONTENT_ITEMS[index % CONTENT_ITEMS.length];
    return {
      index,
      type,
      caption: item.caption,
      thumbnailUrl: `${CONTENT_THUMB_BASE}/${item.thumb}.svg`,
      reach,
      views: reach * (0.85 + seededRatio(`${seed}|views`) * 0.3),
      engagement:
        reach * type.engagementIndex * (0.7 + seededRatio(`${seed}|engagement`) * 0.6),
      clicks: reach * type.clickIndex * (0.7 + seededRatio(`${seed}|clicks`) * 0.6),
    };
  });

  const totalOf = (pick: (_seed: (typeof seeds)[number]) => number): number =>
    seeds.reduce((sum, seed) => sum + pick(seed), 0) || 1;

  const reachTotal = totalOf(seed => seed.reach);
  const viewsTotal = totalOf(seed => seed.views);
  const engagementTotal = totalOf(seed => seed.engagement);
  const clicksTotal = totalOf(seed => seed.clicks);

  return seeds.map(seed => {
    const impressions = Math.round(totals.impressions * (seed.reach / reachTotal));
    const views = Math.round(totals.views * (seed.views / viewsTotal));
    const engagementShare = seed.engagement / engagementTotal;
    const likes = Math.round(totals.likes * engagementShare);
    const comments = Math.round(totals.comments * engagementShare);
    const shares = Math.round(totals.shares * engagementShare);
    const linkClicks = Math.round(totals.linkClicks * (seed.clicks / clicksTotal));
    const publishedAt = dates.length
      ? dates[Math.min(dates.length - 1, Math.round((seed.index / count) * (dates.length - 1)))]
      : endDate;

    return {
      id: `${platform}-${String(seed.index + 1).padStart(3, "0")}`,
      caption: seed.caption,
      typeKey: seed.type.key,
      thumbnailUrl: seed.thumbnailUrl,
      publishedAt,
      impressions,
      views,
      linkClicks,
      likes,
      comments,
      shares,
      engagementRate: percent(likes + comments + shares, impressions),
      ctr: percent(linkClicks, impressions),
      link:
        platform === "tiktok"
          ? `https://www.tiktok.com/@${profile.handle}/video/${7_400_000_000 + seed.index}`
          : `https://story.snapchat.com/@${profile.handle}/${7_400_000_000 + seed.index}`,
    };
  });
};

const buildTypePerformance = (
  platform: OrganicSocialPlatform,
  content: SocialContentRow[]
): SocialTypePerformance[] =>
  PROFILES[platform].contentTypes.map(type => {
    const rows = content.filter(row => row.typeKey === type.key);
    const totals = rows.reduce(
      (accumulator, row) => ({
        views: accumulator.views + row.views,
        impressions: accumulator.impressions + row.impressions,
        likes: accumulator.likes + row.likes,
        comments: accumulator.comments + row.comments,
        shares: accumulator.shares + row.shares,
        linkClicks: accumulator.linkClicks + row.linkClicks,
      }),
      { views: 0, impressions: 0, likes: 0, comments: 0, shares: 0, linkClicks: 0 }
    );

    return {
      key: type.key,
      color: type.color,
      contentCount: rows.length,
      ...totals,
      engagementRate: percent(
        totals.likes + totals.comments + totals.shares,
        totals.impressions
      ),
      ctr: percent(totals.linkClicks, totals.impressions),
    };
  });

export function buildSocialReport(
  platform: OrganicSocialPlatform,
  startDate: string,
  endDate: string
): SocialReportBundle {
  const daily = dailySeries(platform, startDate, endDate);
  const days = daily.length || PROFILE_PERIOD_DAYS;
  const totals = sumDaily(platform, daily, contentCountFor(platform, days));

  const prior = priorPeriod(startDate, endDate);
  const priorDaily = dailySeries(platform, prior.startDate, prior.endDate);
  const priorTotals = sumDaily(
    platform,
    priorDaily,
    contentCountFor(platform, priorDaily.length || days)
  );

  const content = buildContent(platform, totals, startDate, endDate);

  return {
    totals,
    priorTotals,
    daily,
    typePerformance: buildTypePerformance(platform, content),
    content,
  };
}

export type SocialTrendGranularity = "daily" | "monthly";

/** Rolls the daily series into months; followers keep the month's last value. */
export function bucketSocialSeries(
  daily: SocialDailyPoint[],
  granularity: SocialTrendGranularity
): SocialDailyPoint[] {
  if (granularity === "daily") {
    return daily;
  }

  const buckets = new Map<string, SocialDailyPoint>();
  daily.forEach(point => {
    const key = dayjs(point.date).format("YYYY-MM");
    const current = buckets.get(key);
    if (!current) {
      buckets.set(key, { ...point, date: key });
      return;
    }
    const merged: SocialDailyPoint = {
      date: key,
      views: current.views + point.views,
      impressions: current.impressions + point.impressions,
      engagements: current.engagements + point.engagements,
      likes: current.likes + point.likes,
      comments: current.comments + point.comments,
      shares: current.shares + point.shares,
      linkClicks: current.linkClicks + point.linkClicks,
      followerGain: current.followerGain + point.followerGain,
      followers: point.followers,
      engagementRate: null,
      ctr: null,
    };
    merged.engagementRate = percent(merged.engagements, merged.impressions);
    merged.ctr = percent(merged.linkClicks, merged.impressions);
    buckets.set(key, merged);
  });

  return Array.from(buckets.values()).sort((left, right) => left.date.localeCompare(right.date));
}


